import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatCostLong, formatDate } from "@/lib/format";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { useState } from "react";

const PAGE_SIZE = 25;

// Credit purchases are the only ledger rows Stripe invoices.
const INVOICED_TYPES = ["purchase", "auto_recharge"];

const TYPE_LABEL: Record<string, string> = {
  purchase: "Credit purchase",
  auto_recharge: "Auto-recharge",
};

const linkClass = "text-primary underline underline-offset-4 hover:no-underline";

// The Usage page's Invoices tab: credit purchases that carry a Stripe invoice,
// newest first. Pages follow the ledger's purchase rows; a purchase whose
// invoice hasn't arrived yet is left out until it has one.
export function InvoicesSection() {
  const [offset, setOffset] = useState(0);
  const { data, isError, error, isPending } = useQuery({
    // Under "billing-transactions" so a checkout return's refetch covers it.
    queryKey: ["billing-transactions", "invoices", offset],
    queryFn: () => api.billing.transactions(PAGE_SIZE, offset, INVOICED_TYPES),
    placeholderData: keepPreviousData,
  });

  if (isPending) return <LoadingIndicator />;
  if (isError) return <InlineError error={error ?? "Failed to load invoices"} />;

  const invoices = data.entries.filter((row) => row.invoiceUrl || row.invoicePdf);
  const paged = data.total > PAGE_SIZE;

  if (invoices.length === 0 && !paged) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
        <FileText className="size-6 text-muted-foreground" />
        <div className="text-sm font-medium">No invoices yet</div>
        <p className="max-w-sm text-sm text-muted-foreground">
          An invoice is issued each time you buy credits or auto-recharge tops up
          your balance.
        </p>
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-base font-semibold">Invoices</h2>
        <p className="text-sm text-muted-foreground">
          One invoice for each credit purchase, newest first.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="py-2 pr-3 font-medium">Date</th>
            <th className="py-2 pr-3 font-medium">Description</th>
            <th className="py-2 pr-3 font-medium text-right">Amount</th>
            <th className="py-2 pl-8 font-medium">Invoice</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {invoices.map((row) => (
            <tr key={row.id}>
              <td className="py-3 pr-3 whitespace-nowrap">{formatDate(row.createdAt)}</td>
              <td className="py-3 pr-3">{TYPE_LABEL[row.type] ?? "Credit purchase"}</td>
              <td className="py-3 pr-3 text-right tabular-nums">{formatCostLong(row.amount)}</td>
              <td className="py-3 pl-8 whitespace-nowrap">
                <span className="inline-flex items-center gap-4">
                  {row.invoiceUrl && (
                    <a href={row.invoiceUrl} target="_blank" rel="noreferrer" className={linkClass}>
                      View invoice
                    </a>
                  )}
                  {row.invoicePdf && (
                    <a href={row.invoicePdf} className={linkClass}>
                      PDF
                    </a>
                  )}
                </span>
              </td>
            </tr>
          ))}
          {invoices.length === 0 && (
            <tr>
              <td colSpan={4} className="py-6 text-center text-muted-foreground">
                No invoices among these purchases.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {paged && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span className="tabular-nums">
            Purchases {data.offset + 1}–{Math.min(data.offset + data.entries.length, data.total)} of{" "}
            {data.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              disabled={data.offset === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              disabled={data.offset + data.entries.length >= data.total}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
